import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import './styles/index.css';
import './i18n/config';
import { applyBrandTheme } from './config/brand';

applyBrandTheme();
import { AuthProvider, useAuth } from './auth/AuthContext';
import { ImpersonationProvider } from './auth/ImpersonationContext';
import { routeTree } from './routeTree.gen';
import type { User } from 'oidc-client-ts';
import { Toaster } from './components/Sonner/Sonner';
import Loader from './components/Loader/Loader';
import * as Sentry from "@sentry/react";
import { TooltipProvider } from './components/General/Tooltip';
import { setPreviousPathname } from './lib/previousRoute';

// Definisci il tipo del router context
interface RouterContext {
  auth: {
    user: User | null;
    isLoading: boolean;
    login: () => Promise<void>;
    logout: () => Promise<void>;
  };
}

// 1. Crea l'istanza del router FUORI dal componente.
// Passiamo 'auth' come undefined inizialmente, lo inietteremo nel provider.
const router = createRouter({
  routeTree,
  context: {
    auth: undefined!,
  } as RouterContext,
  scrollRestoration: true,
});

// 2. Crea QueryClient per TanStack Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      retry: 1,
      refetchOnWindowFocus: false,
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
    },
  },
});

// 3. Crea il persister per salvare la cache in localStorage
const persister = createSyncStoragePersister({
  storage: window.localStorage,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Keep track of the route the user is navigating away from, so any route
// that needs it (e.g. analytics events) can read where the user came from.
router.subscribe('onBeforeRouteMount', ({ fromLocation, toLocation }) => {
  if (fromLocation && fromLocation.pathname !== toLocation.pathname) {
    setPreviousPathname(fromLocation.pathname);
  }
});

// 4. Initialize sentry
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  sendDefaultPii: true,
  integrations: [
    Sentry.tanstackRouterBrowserTracingIntegration(router),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  ignoreErrors: [
    'No end session endpoint',
  ],
});

// 5. Crea un componente interno che consuma il context e fornisce i dati al router
function InnerApp() {
  const auth = useAuth();

  // Attendi il caricamento dell'autenticazione prima di renderizzare il router
  if (auth.isLoading) {
    return <Loader fullScreen />;
  }

  return (
    <Sentry.ErrorBoundary fallback={<p>An unexpected error occurred.</p>}>
      <RouterProvider
        router={router}
        context={{ auth } as RouterContext}
      />
      <ReactQueryDevtools initialIsOpen={false} />
    </Sentry.ErrorBoundary>
  );
}
const rootElement = document.getElementById('root')!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{ persister }}
      >
        <AuthProvider>
          <ImpersonationProvider>
            <TooltipProvider>
            <Toaster />
            <InnerApp />
            </TooltipProvider>
          </ImpersonationProvider>
        </AuthProvider>
      </PersistQueryClientProvider>
    </StrictMode>,
  );
}

document.addEventListener('copy', (e) => {
  e.preventDefault();
  e.stopPropagation();
});

document.addEventListener('cut', (e) => {
  e.preventDefault();
  e.stopPropagation();
});

document.addEventListener('keydown', (e) => {
  if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C')) {
    e.preventDefault();
    e.stopPropagation();
  }
});
